/*
 * <cart-upsell> — Swiper slider for the cart drawer upsell block.
 *
 * The block markup (snippets/cart-upsell.liquid) lives inside the cart drawer's
 * `.cart-drawer__inner` hydration region, so it is re-rendered server-side on
 * every cart update and morphed into place. The morph resets Swiper's added
 * classes / inline styles and adds/removes slide nodes, which desyncs the live
 * Swiper instance. We therefore watch the slide wrapper's childList and, when it
 * changes (a product was added → filtered out, or removed → came back), destroy
 * and re-initialise Swiper on the fresh, clean DOM.
 *
 * Swiper (v11) is loaded once from the same CDN + `#swiper-lib` guard used by
 * sections/product-carousel.liquid. If it fails to load, the CSS fallback in
 * cart-upsell.liquid keeps a static two-per-row layout (progressive enhancement).
 */
(function () {
  var SWIPER_JS = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
  var SWIPER_CSS = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';

  /**
   * Ensure the Swiper library is available, then run `cb`.
   * @param {() => void} cb
   */
  function loadSwiper(cb) {
    if (window.Swiper) {
      cb();
      return;
    }

    var existing = document.getElementById('swiper-lib');
    if (existing) {
      existing.addEventListener('load', cb, { once: true });
      // If it already finished loading before this listener attached.
      if (window.Swiper) cb();
      return;
    }

    if (!document.getElementById('swiper-lib-css')) {
      var css = document.createElement('link');
      css.rel = 'stylesheet';
      css.id = 'swiper-lib-css';
      css.href = SWIPER_CSS;
      document.head.appendChild(css);
    }

    var js = document.createElement('script');
    js.id = 'swiper-lib';
    js.src = SWIPER_JS;
    js.addEventListener('load', cb, { once: true });
    document.head.appendChild(js);
  }

  var SWIPER_CONFIG = {
    // Mobile shows 2.5 per view (peek of the next card); desktop (>= 750px) shows 3.
    slidesPerView: 2.5,
    spaceBetween: 12,
    // Lock sliding + hide the arrows when all slides already fit (adapts per
    // breakpoint: > 2 on mobile, > 3 on desktop).
    watchOverflow: true,
    loop: false,
    breakpoints: {
      750: {
        slidesPerView: 3,
      },
    },
  };

  class CartUpsell extends HTMLElement {
    connectedCallback() {
      var self = this;
      loadSwiper(function () {
        self.init();
      });
    }

    disconnectedCallback() {
      this.teardown();
    }

    init() {
      if (!window.Swiper || !this.isConnected) return;

      this.container = this.querySelector('.swiper');
      var wrapper = this.querySelector('.swiper-wrapper');
      if (!this.container || !wrapper) return;

      this.swiper = new window.Swiper(
        this.container,
        Object.assign({}, SWIPER_CONFIG, {
          navigation: {
            nextEl: this.querySelector('.cart-upsell__nav--next'),
            prevEl: this.querySelector('.cart-upsell__nav--prev'),
          },
        })
      );

      // Re-init after the cart drawer re-renders (morph changes the slide set).
      // childList only: with loop:false Swiper never adds/removes slide nodes,
      // so our own re-init can't retrigger the observer → no loop.
      var self = this;
      this.observer = new MutationObserver(function () {
        if (self.pending) return;
        self.pending = requestAnimationFrame(function () {
          self.pending = 0;
          self.reinit();
        });
      });
      this.observer.observe(wrapper, { childList: true });
    }

    reinit() {
      if (!window.Swiper || !this.isConnected) return;

      // Re-query in case the morph replaced the container node.
      this.container = this.querySelector('.swiper');
      var wrapper = this.querySelector('.swiper-wrapper');
      if (!this.container || !wrapper) return;

      if (this.swiper) {
        try {
          this.swiper.destroy(true, true);
        } catch (e) {
          /* instance already detached by the morph — ignore */
        }
        this.swiper = null;
      }

      this.swiper = new window.Swiper(
        this.container,
        Object.assign({}, SWIPER_CONFIG, {
          navigation: {
            nextEl: this.querySelector('.cart-upsell__nav--next'),
            prevEl: this.querySelector('.cart-upsell__nav--prev'),
          },
        })
      );
    }

    teardown() {
      if (this.pending) {
        cancelAnimationFrame(this.pending);
        this.pending = 0;
      }
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.swiper) {
        try {
          this.swiper.destroy(true, true);
        } catch (e) {
          /* ignore */
        }
        this.swiper = null;
      }
    }
  }

  if (!customElements.get('cart-upsell')) {
    customElements.define('cart-upsell', CartUpsell);
  }
})();
